/**
 * P5 분석·관계 페이지 (DESIGN §8.1 · RUNBOOK P5).
 *
 * 이 페이지가 지켜야 할 다섯 가지:
 *
 * 1. **그래프는 standard 한 벌뿐이다.** `graph_nodes` 에 mode 축이 없다 (§8.1.1).
 *    상단 모드 토글을 바꿔도 그림이 안 바뀌는데, 그 사실을 적지 않으면 버그로 보인다.
 * 2. **CORRELATED 0건은 실패가 아니다.** 관측 4일로는 30일 기준을 채우는 쌍이 없어
 *    검정을 시작조차 못 했다. "관계가 없다"가 아니라 **"아직 판정 불가"** 로 읽혀야
 *    하므로 진단 숫자를 접지 않고 화면에 인쇄한다.
 *    다만 그 문장은 **검정한 쌍이 0개일 때만** 참이다. 관측이 쌓여 검정이 시작되면 같은
 *    문장이 "검정한 쌍 5,048개" 옆에서 거짓이 되므로 `n_pairs_tested` 로 문구를 가른다
 *    (`n_edges` 로 가르면 검정은 했는데 통과한 쌍이 없는 경우까지 "판정 불가"라 말한다).
 * 3. **고립 노드를 화면 맞춤에서 뺀다.** ForceAtlas2 가 엣지 없는 노드를 군집 밖
 *    수천 단위로 밀어내므로(실측 반경 3,108~4,093 vs 연결 노드 ≤551) 전체 범위로
 *    맞추면 본 군집이 점 하나가 된다. 대신 목록으로 남긴다 — 지우지 않는다.
 * 4. **임계는 절댓값이다.** 음의 상관도 관계다 (§8.1.1).
 * 5. **드릴다운 위치를 항상 보여준다.** 브레드크럼은 차트/표 어느 뷰에서도 보이도록
 *    본문 밖(`controls`)에 둔다 — 본문에 두면 표 보기로 바꿀 때 사라진다 (§8.6.5 규칙 4).
 */

import { useEffect, useMemo, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { SidePanel } from '@/components/SidePanel'
import { Headline } from '@/components/WidgetShell'
import { CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import { GraphView } from './analysis/GraphView'
import {
  ALL_EDGE_TYPES,
  DEFAULT_MIN_WEIGHT,
  EDGE_TYPES,
  WEIGHT_FLOOR,
  trustMark,
  useGraph,
  type EdgeType,
} from './analysis/useGraph'
import './AnalysisPage.css'

/**
 * 그래프 높이. 노드 146개를 표준 카드 높이(240)에 넣으면 마크가 서로 겹쳐 무리를
 * 구분할 수 없다. 새 상수를 만들지 않고 `CHART_H` 에서 파생해 다른 카드와 같은
 * 눈금 위에 둔다 (§8.6.6).
 */
const GRAPH_H = CHART_H * 2

/** 엣지 표는 |가중치| 상위 이만큼만 싣는다 — 3,777 행을 DOM 에 그리면 표가 멈춘다 */
const EDGE_TABLE_LIMIT = 300

interface Crumb {
  id: string
  label: string
}

export function AnalysisPage() {
  const [trail, setTrail] = useState<Crumb[]>([])
  const [types, setTypes] = useState<EdgeType[]>(ALL_EDGE_TYPES)
  // 슬라이더는 끄는 동안 값이 계속 바뀐다. 그대로 요청하면 드래그 한 번에 수십 번
  // 왕복하므로 잠깐 멈춘 뒤의 값만 조회에 쓴다
  const [weightDraft, setWeightDraft] = useState(DEFAULT_MIN_WEIGHT)
  const [minWeight, setMinWeight] = useState(DEFAULT_MIN_WEIGHT)

  useEffect(() => {
    const t = setTimeout(() => setMinWeight(weightDraft), 180)
    return () => clearTimeout(t)
  }, [weightDraft])

  const focus = trail.length > 0 ? (trail[trail.length - 1]?.id ?? null) : null
  const g = useGraph({ focus, types, minWeight })

  const labelOf = useMemo(() => {
    const m = new Map(g.allNodes.map((n) => [n.node_id, n.label]))
    return (id: string) => m.get(id) ?? id
  }, [g.allNodes])

  const edgeRows = useMemo(
    () => [...g.edges].sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight)),
    [g.edges],
  )
  const nodeRows = useMemo(
    () => [...g.allNodes].sort((a, b) => b.n_chargers - a.n_chargers),
    [g.allNodes],
  )

  const corr = g.correlation
  const sim = g.similarity
  const layout = g.layout
  const nAvailMissing = g.allNodes.filter((n) => n.availability == null).length
  /**
   * 화면에서 빠진 사유는 하나가 아니다. 임계를 올리면 좌표가 군집 *안*에 있는 대형
   * 사업자도 선이 끊겨 목록으로 내려가는데, 그들에게 "ForceAtlas2 가 군집 밖으로
   * 밀어냈다"고 적으면 없는 사유를 붙이는 것이다 — 사유별로 세어 따로 말한다.
   */
  const nIsoDropped = g.isolated.filter((i) => i.kind === 'dropped').length
  const nIsoThreshold = g.isolated.filter((i) => i.kind === 'threshold').length
  const nIsoNoXy = g.isolated.filter((i) => i.kind === 'no-xy').length
  /** 범례에 "0건"을 손으로 적어 두면 데이터가 바뀐 날 범례만 거짓말한다 */
  const nCorrEdges = g.edges.filter((e) => e.edge_type === 'CORRELATED').length
  /**
   * nCorrEdges 는 임계만이 아니라 타입 토글에도 걸린다. 타입을 끈 화면에 "임계를 내리면
   * 보인다"고 적으면 임계를 이미 내렸어도 0건이라 거짓이 된다 — 안 보이는 사유를 나눠 말한다
   */
  const corrTypeOn = types.includes('CORRELATED')
  /** 차분 전 상관·관측일은 CORRELATED 에만 있다. 결측을 0 으로 채우지 않으므로 개수를 밝힌다 */
  const nEdgeDiagMissing = g.edges.filter((e) => e.r_raw == null).length

  const drill = (nodeId: string) => {
    setTrail((prev) =>
      prev.some((c) => c.id === nodeId) ? prev : [...prev, { id: nodeId, label: labelOf(nodeId) }],
    )
  }

  const controls = (
    <div className="apage__controls">
      <nav className="apage__crumbs" aria-label="그래프 위치" data-testid="breadcrumb">
        <button
          type="button"
          className={`apage__crumb${trail.length === 0 ? ' is-on' : ''}`}
          aria-current={trail.length === 0 ? 'true' : undefined}
          onClick={() => setTrail([])}
        >
          전체 그래프 (L0)
        </button>
        {trail.map((c, i) => (
          <span key={c.id} className="apage__crumbstep">
            <span className="apage__crumbsep" aria-hidden="true">
              ›
            </span>
            <button
              type="button"
              className={`apage__crumb${i === trail.length - 1 ? ' is-on' : ''}`}
              aria-current={i === trail.length - 1 ? 'true' : undefined}
              onClick={() => setTrail((prev) => prev.slice(0, i + 1))}
            >
              {c.label}
            </button>
          </span>
        ))}
        <span className="apage__crumbhint">
          {trail.length === 0
            ? '노드를 더블클릭하면 그 노드의 1-hop 로컬 그래프로 들어갑니다'
            : '1-hop 로컬 그래프 — 루트에 붙은 엣지만 옵니다 (이웃끼리의 관계는 포함되지 않습니다)'}
        </span>
      </nav>

      <div className="apage__knobs">
        <fieldset className="apage__types">
          <legend>엣지 타입</legend>
          {EDGE_TYPES.map((t) => (
            <label key={t.key} className="apage__type">
              <input
                type="checkbox"
                checked={types.includes(t.key)}
                onChange={() =>
                  setTypes((prev) =>
                    prev.includes(t.key) ? prev.filter((k) => k !== t.key) : [...prev, t.key],
                  )
                }
              />
              <span className="apage__typename">{t.label}</span>
              <span className="apage__typehint">{t.hint}</span>
            </label>
          ))}
        </fieldset>

        <div className="apage__slider">
          <label htmlFor="minweight">
            임계 <b>|가중치| ≥ {weightDraft.toFixed(2)}</b>
          </label>
          <input
            id="minweight"
            type="range"
            min={WEIGHT_FLOOR}
            max={1}
            step={0.01}
            value={weightDraft}
            onChange={(e) => setWeightDraft(Number(e.target.value))}
          />
          <span className="apage__sliderhint">
            절댓값 기준입니다 — 음의 상관도 관계이므로 부호로 자르지 않습니다.
            {sim && corr
              ? ` 빌드 하한은 코사인 ${sim.min_cosine.toFixed(2)} · |r| ${corr.min_abs_r.toFixed(2)} 이라 그 아래로 내려도 엣지가 늘지 않습니다.`
              : ''}
          </span>
        </div>
      </div>

      <p className="apage__counts" data-testid="graph-counts">
        그린 것 <b className="num">노드 {int(g.nodes.length)}</b>
        <span className="apage__dot" aria-hidden="true">
          ·
        </span>
        <b className="num">엣지 {int(g.edges.length)}</b>
        <span className="apage__dot" aria-hidden="true">
          ·
        </span>
        고립 <span className="num">{int(g.isolated.length)}</span>곳은 오른쪽 목록에
        {g.nOrphanEdges > 0 && (
          <>
            <span className="apage__dot" aria-hidden="true">
              ·
            </span>
            양끝 노드가 응답에 없어 못 그린 엣지 <span className="num">{int(g.nOrphanEdges)}</span>
          </>
        )}
      </p>
    </div>
  )

  const table = (
    <>
      <h4 className="apage__tabletitle">노드 {int(g.allNodes.length)}개</h4>
      <div className="ctable">
        <table className="data-table" data-testid="node-table">
          <caption className="sr-only">그래프 노드 — 라벨·충전기·가동률·커버리지·엣지 수</caption>
          <thead>
            <tr>
              <th scope="col">사업자</th>
              <th scope="col" className="ta-r">
                충전기
              </th>
              <th scope="col" className="ta-r">
                가동률
              </th>
              <th scope="col" className="ta-r">
                커버리지
              </th>
              <th scope="col">테두리</th>
              <th scope="col" className="ta-r">
                엣지
              </th>
            </tr>
          </thead>
          <tbody>
            {nodeRows.map((n) => {
              const deg = g.degree.get(n.node_id) ?? 0
              return (
                <tr key={n.node_id}>
                  <td>
                    {n.label} <span className="mono apage__id">{n.node_id}</span>
                  </td>
                  <td className="num ta-r">{int(n.n_chargers)}</td>
                  <td className="num ta-r">{pct(n.availability)}</td>
                  <td className="num ta-r">{pct(n.coverage, 1)}</td>
                  <td>{trustMark(n.coverage)}</td>
                  <td className="num ta-r">{deg === 0 ? '0 (고립)' : int(deg)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <h4 className="apage__tabletitle">
        엣지 {int(g.edges.length)}개
        {edgeRows.length > EDGE_TABLE_LIMIT && (
          <span className="apage__tablenote">
            {' '}
            — |가중치| 상위 {int(EDGE_TABLE_LIMIT)}개만 싣습니다
          </span>
        )}
      </h4>
      <div className="ctable">
        <table className="data-table" data-testid="edge-table">
          <caption className="sr-only">그래프 엣지 — 양끝·타입·가중치·차분 전 상관·관측일</caption>
          <thead>
            <tr>
              <th scope="col">한쪽</th>
              <th scope="col">다른 쪽</th>
              <th scope="col">타입</th>
              <th scope="col" className="ta-r">
                가중치
              </th>
              <th scope="col" className="ta-r">
                차분 전 r
              </th>
              <th scope="col" className="ta-r">
                관측일
              </th>
            </tr>
          </thead>
          <tbody>
            {edgeRows.slice(0, EDGE_TABLE_LIMIT).map((e) => (
              <tr key={`${e.src}|${e.dst}|${e.edge_type}`}>
                <td>{labelOf(e.src)}</td>
                <td>{labelOf(e.dst)}</td>
                <td>{e.edge_type}</td>
                <td className="num ta-r">{e.weight.toFixed(3)}</td>
                <td className="num ta-r">{e.r_raw == null ? '—' : e.r_raw.toFixed(3)}</td>
                <td className="num ta-r">{e.n_days == null ? '—' : int(e.n_days)}</td>
              </tr>
            ))}
            {edgeRows.length === 0 && (
              <tr>
                <td colSpan={6}>
                  이 조건에서 그릴 엣지가 없습니다 — 타입을 켜거나 임계를 낮춰 보세요.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )

  return (
    <div className="apage">
      <header className="apage__intro">
        <p className="apage__lead">
          사업자 <strong>{int(g.allNodes.length)}곳</strong>을 점으로, 포트폴리오가 닮은 쌍을 선으로
          이은 그림입니다. 가까이 모인 무리가 서로 닮은 사업자 집단이고, 좌표는{' '}
          <strong>Gold 빌드가 고정</strong>했으므로 새로고침해도 배치가 바뀌지 않습니다.
        </p>
        <p className="apage__fixed">
          <strong>이 그래프는 standard 고정입니다.</strong> 상단 모드 토글(설비 고장만 · 통신 포함 ·
          이용자 관점)과 필터 바를 바꿔도 그림은 바뀌지 않습니다 — <code>graph_nodes</code> 에 mode
          축이 없고 그래프는 빌드 산출물이라 필터를 인자로 받지 않기 때문입니다 (§8.1.1). 다른
          페이지의 숫자와 달리 이 화면만 토글에 반응하지 않는 것은 버그가 아닙니다.
        </p>
      </header>

      <div className="apage__grid">
        <ChartFrame
          title="사업자 관계 그래프"
          question="누가 누구와 닮았나 — 포트폴리오 벡터의 코사인 유사도로 이은 관계망"
          meta={g.meta}
          // /graph 의 meta.coverage 는 1.0 고정 자리표시자다. 관측 커버리지가 아니므로
          // 마이크로바를 띄우면 "전부 관측했다"는 거짓말이 된다 — 노드별 커버리지는
          // 테두리와 표가 말한다
          hideCoverage
          headline={
            <Headline
              value={`${int(g.nodes.length)}/${int(g.allNodes.length)}`}
              unit="그린 노드 (고립 제외)"
            />
          }
          controls={controls}
          reading={`사업자 ${int(g.allNodes.length)}곳 중 현재 임계에서 연결이 있는 ${int(
            g.nodes.length,
          )}곳을 그렸습니다. 두 점이 선으로 이어져 있으면 지역·충전기 타입·시설 유형·용량의 분포가 닮았다는 뜻이며, 가동률이 비슷하다는 뜻이 아닙니다.`}
          encoding={[
            {
              role: '가로축',
              what: 'ForceAtlas2 좌표 x — 값이 아니다',
              detail:
                '가로 위치 자체에는 의미가 없습니다. 힘 기반 배치가 서로 많이 이어진 노드를 끌어당겨 만든 그림이므로 읽을 것은 좌표가 아니라 "누가 누구 옆에 있는가"뿐입니다. 좌표는 Gold 빌드에서 한 번 계산해 고정했고 클라이언트에서 다시 돌리지 않습니다 — 볼 때마다 그림이 달라지면 어제 본 무리를 다시 찾을 수 없습니다.',
            },
            {
              role: '세로축',
              what: 'ForceAtlas2 좌표 y — 값이 아니다',
              detail:
                '가로축과 같습니다. 위아래가 좋고 나쁨을 뜻하지 않으며, 축 눈금을 그리지 않은 이유도 그것입니다.',
            },
            {
              role: '점 크기',
              what: '충전기 수 (지름 ∝ √n)',
              detail:
                '넓이가 아니라 길이로 읽히도록 √n 으로 매핑했습니다. 다만 하한이 3px 이라 충전기 2,400기 아래는 서로 같은 크기로 보입니다 — 정확한 값은 표 보기에 있습니다.',
            },
            {
              role: '점 색',
              what: '단일 계열 (정체성이지 상태가 아니다)',
              detail:
                '상태색(정상·고장·모름)을 쓰지 않았습니다. 노드는 사업자라는 정체성이지 상태가 아니어서, 색을 상태로 쓰면 파란 점이 "정상"으로 읽힙니다.',
            },
            {
              role: '테두리',
              what: '커버리지 신뢰도 (실선 ≥80% · 파선 50~80% · 점선 <50%)',
              detail:
                '숫자를 못 믿을수록 테두리가 끊깁니다. 실측 커버리지 중앙이 낮아 대부분이 파선·점선으로 그려지는 것이 정상입니다. 색이 아니라 모양으로 말하는 이유는 색각 이상에서도 구분되어야 하기 때문입니다.',
            },
            {
              role: '선',
              what: `관계 (굵기 ∝ |가중치| · 현재 임계 ${minWeight.toFixed(2)})`,
              detail:
                '임계는 절댓값 기준입니다. 부호로 자르면 "반대로 움직인다"는 관계가 통째로 사라지기 때문입니다. 선이 많이 겹칠수록 진해지도록 낮은 투명도로 그렸으므로 진한 부분이 곧 밀집입니다.',
            },
            {
              role: '이중 링',
              what: '드릴다운으로 들어온 노드',
              detail:
                '로컬 그래프에서 어느 노드로 들어왔는지를 그림에도 남깁니다. 브레드크럼과 같은 정보를 두 곳에 두는 이유는 캔버스만 보고 있을 때도 위치를 알아야 하기 때문입니다.',
            },
          ]}
          // 테두리(실선·파선·점선) 견본은 캔버스 바로 위에 있다 — 카드 범례의 원형
          // 견본은 대시를 그리지 못해 "파선" 옆에 실선이 찍히기 때문이다
          legend={[
            {
              label: `SIMILAR_TO (포트폴리오 유사) — ${int(g.edges.length - nCorrEdges)}건`,
              color: cssVar('--c-cat-1', '#0066cc'),
              pattern: 'line' as const,
            },
            {
              label: `CORRELATED (이상률 동조) — ${int(nCorrEdges)}건`,
              color: cssVar('--c-cat-2', '#a35200'),
              pattern: 'line' as const,
            },
          ]}
          isLoading={g.isLoading}
          error={g.error}
          isEmpty={!g.isLoading && g.allNodes.length === 0}
          onRetry={g.refetch}
          table={table}
          notes={[
            // 아래 두 각주의 "0건 = 판정 불가" 문장은 검정한 쌍이 0개일 때만 참이다 (머리말 2)
            corr && corr.n_pairs_tested === 0
              ? {
                  label: 'CORRELATED 가 0건인 것은 "관계가 없다"가 아니다.',
                  text: `관측 ${int(corr.n_observed_days)}일로는 차분 표본이 최대 ${int(
                    corr.max_diff_pairs,
                  )}개뿐이라 기준(차분 표본 ${int(
                    corr.min_days_required,
                  )}개)을 채우는 쌍이 없습니다. 검정한 쌍 ${int(
                    corr.n_pairs_tested,
                  )}개 · 창이 짧아 검정조차 못 한 쌍 ${int(corr.n_pairs_short_window)}개입니다. 기준을 낮추지 않고 0건으로 둡니다.`,
                }
              : '',
            corr && corr.n_pairs_tested === 0
              ? {
                  label: '"차분이 지운 허위 엣지 0건" 도 같은 뜻이다.',
                  text: `n_spurious_removed_by_diff = ${int(
                    corr.n_spurious_removed_by_diff,
                  )} 는 "허위 관계가 없었다"가 아니라 검정한 쌍이 ${int(
                    corr.n_pairs_tested,
                  )}개라 뺄 것이 없었다는 뜻입니다. 같은 dict 의 n_pairs_tested 를 함께 보아야 오독하지 않습니다.`,
                }
              : '',
            corr && corr.n_pairs_tested > 0
              ? {
                  label: `검정한 쌍 ${int(corr.n_pairs_tested)}개 중 기준을 넘은 CORRELATED 는 ${int(
                    corr.n_edges,
                  )}건이다.`,
                  text: `차분 후 |r| ≥ ${corr.min_abs_r.toFixed(2)} · BH-FDR q < ${corr.fdr_q.toFixed(
                    2,
                  )} 를 함께 만족한 쌍만 남겼습니다. 창이 짧아 검정조차 못 한 쌍 ${int(
                    corr.n_pairs_short_window,
                  )}개는 "관계가 없다"가 아니라 판정 불가입니다.${
                    corr.n_edges === 0
                      ? ''
                      : corrTypeOn
                        ? ` 범례의 건수는 현재 임계 ${minWeight.toFixed(
                            2,
                          )}에서 그린 것만 셉니다 — |r| 이 그 아래인 엣지는 임계를 ${corr.min_abs_r.toFixed(
                            2,
                          )}까지 내려야 보입니다.`
                        : ' 지금은 "이상률 동조" 타입이 꺼져 있어 범례의 CORRELATED 는 임계와 무관하게 0건입니다 — 타입을 켜야 보입니다.'
                  }`,
                }
              : '',
            corr && corr.n_pairs_tested > 0
              ? {
                  label: `"차분이 지운 허위 엣지 ${int(
                    corr.n_spurious_removed_by_diff,
                  )}건" 은 차분 전에만 통과한 쌍이다.`,
                  text: `차분 전 상관으로는 기준을 넘었지만 1차 차분 뒤 탈락한 쌍입니다. 계절·요일·API 장애 같은 공통 추세가 만든 관계라 엣지로 세지 않았습니다. 검정한 쌍 ${int(
                    corr.n_pairs_tested,
                  )}개 위에서 센 값이므로 n_pairs_tested 와 함께 읽어야 합니다.`,
                }
              : '',
            {
              label: '모드 토글·필터가 이 그래프를 바꾸지 않는다.',
              text: 'graph_nodes 에 mode 축이 없어 그래프는 standard 한 벌만 존재하며, 빌드 산출물이라 필터를 인자로 받지 않습니다 (§8.1.1).',
            },
            nIsoDropped > 0
              ? {
                  label: `빌드가 엣지 계산에서 뺀 ${int(nIsoDropped)}곳은 좌표가 군집 밖이다.`,
                  text: 'ForceAtlas2 가 엣지 없는 노드를 군집 밖 수천 단위로 밀어내므로 전체 범위로 화면을 맞추면 본 군집이 점 하나가 됩니다. 그래서 화면 맞춤에서 뺐습니다.',
                }
              : '',
            nIsoThreshold > 0
              ? {
                  label: `현재 임계에서 선이 끊긴 ${int(nIsoThreshold)}곳은 사정이 다르다.`,
                  text: '이들의 좌표는 군집 안이라 화면 맞춤과는 무관합니다. 그릴 선이 없어 캔버스에서 뺐을 뿐이며, 임계를 낮추면 제자리로 돌아옵니다 — "군집 밖으로 밀려났다"로 읽으면 안 됩니다.',
                }
              : '',
            sim
              ? {
                  label: '유사도 계산에서 빠진 사업자가 있다.',
                  text: `충전기 ${int(sim.min_chargers)}기 미만 ${int(
                    sim.n_dropped_min_chargers,
                  )}곳은 포트폴리오 벡터가 한두 칸에 몰려 코사인이 쉽게 1 이 되므로 엣지 계산에서 제외했습니다 — 관계가 없는 것이 아니라 재지 않은 것입니다.`,
                }
              : '',
            nAvailMissing > 0
              ? {
                  label: `가동률이 없는 노드 ${int(nAvailMissing)}곳.`,
                  text: '관측이 없어 계산되지 않은 것이므로 0% 가 아니라 — 로 적었습니다.',
                }
              : '',
            nEdgeDiagMissing > 0
              ? {
                  label: `엣지 표의 "차분 전 r"·"관측일"이 ${int(
                    nEdgeDiagMissing,
                  )}/${int(g.edges.length)}행에서 — 다.`,
                  text: '두 값은 CORRELATED 의 검정 산출물이라 SIMILAR_TO 엣지에는 애초에 없습니다. 값이 0 이라는 뜻이 아니라 그 축으로 재지 않았다는 뜻이므로 0 으로 채우지 않고 — 로 둡니다.',
                }
              : '',
            trail.length > 0
              ? {
                  label: '로컬 그래프는 별 모양이다.',
                  text: '1-hop 응답은 루트에 붙은 엣지만 담습니다. 이웃끼리 서로 이어져 있어도 여기서는 선이 그려지지 않으니 "이웃들은 서로 무관하다"로 읽으면 안 됩니다.',
                }
              : '',
            edgeRows.length > EDGE_TABLE_LIMIT
              ? {
                  label: `엣지 표를 ${int(EDGE_TABLE_LIMIT)}개로 잘랐다.`,
                  text: `|가중치| 상위 ${int(EDGE_TABLE_LIMIT)}개만 싣습니다. 나머지 ${int(
                    edgeRows.length - EDGE_TABLE_LIMIT,
                  )}개는 임계를 올려 좁힌 뒤 확인하세요.`,
                }
              : '',
            g.meta?.pruned
              ? {
                  label: '서버가 상한에서 잘랐다.',
                  text: 'meta.pruned=true — 노드 2,000 · 엣지 10,000 상한을 넘어 응답이 잘렸습니다.',
                }
              : '',
          ]}
        >
          {g.nodes.length === 0 && g.allNodes.length > 0 ? (
            <div className="verdict-note" role="status">
              <p>
                <strong>이 조건에서는 그릴 관계가 없습니다.</strong> 노드{' '}
                {int(g.allNodes.length)}곳이 모두 현재 임계·타입에서 연결이 끊겼습니다.
              </p>
              <p className="verdict-note__hint">
                "관계가 없다"가 아니라 <em>"지금 조건에서 안 보인다"</em>입니다. 임계를 낮추거나
                엣지 타입을 켜면 다시 나타납니다.
              </p>
            </div>
          ) : (
            <GraphView
              nodes={g.nodes}
              edges={g.edges}
              focus={focus}
              height={GRAPH_H}
              onDrill={drill}
            />
          )}
        </ChartFrame>
      </div>

      {/* 우 패널은 셸이 갖는다 (DESIGN §8.6.8) — 지도·분석만 쓰는 슬롯.
          aria-label 이 없던 것도 여기서 고친다 */}
      <SidePanel>
        <aside className="apanel" aria-label="그래프 해설">
          <section className="apanel__block" data-testid="corr-diag">
            <h3 className="apanel__title">
              {corr && corr.n_edges > 0
                ? `CORRELATED 엣지 ${int(corr.n_edges)}건의 근거`
                : 'CORRELATED 엣지 0건의 사유'}
            </h3>
            {corr ? (
              <>
                {/* 기준값의 단위는 '일'이 아니라 '차분 표본 개수'다 (manifest reasons) */}
                {/* "판정 불가"는 검정한 쌍이 0개일 때만 참이다 — n_edges 가 아니라 이것으로 가른다 */}
                {corr.n_pairs_tested === 0 ? (
                  <p className="apanel__lead">
                    <strong>"관계가 없다"가 아니라 "아직 판정할 수 없다"</strong>입니다. 관측{' '}
                    <span className="num">{int(corr.n_observed_days)}</span>일로는 기준(차분 표본{' '}
                    <span className="num">{int(corr.min_days_required)}</span>개)을 채우는 쌍이
                    하나도 없어 검정을 시작조차 못 했습니다.
                  </p>
                ) : (
                  <p className="apanel__lead">
                    <strong>
                      검정을 마쳤고, 기준을 넘은 쌍이 <span className="num">{int(corr.n_edges)}</span>
                      건입니다.
                    </strong>{' '}
                    관측 <span className="num">{int(corr.n_observed_days)}</span>일에서 차분 표본{' '}
                    <span className="num">{int(corr.min_days_required)}</span>개 이상을 갖춘 쌍만
                    검정해, 차분 후 |r| ≥ {corr.min_abs_r.toFixed(2)} · BH-FDR q &lt;{' '}
                    {corr.fdr_q.toFixed(2)}를 함께 만족한 쌍을 엣지로 남겼습니다. 창이 짧아 검정하지
                    못한 쌍은 "관계가 없다"가 아니라 판정 불가로 남습니다.
                  </p>
                )}
                {/* 빌드 하한(min_abs_r)과 페이지 기본 임계가 달라, 빌드에는 있는데 첫 화면에는
                    안 그려지는 엣지가 생긴다. 범례의 "0건"과 위의 건수가 어긋나 보이는 이유를
                    숫자로 적는다 — "모두 임계 아래"라고 단정하지 않고 지금 그린 수를 센다 */}
                {corr.n_edges > 0 && (
                  <p className="apanel__why">
                    빌드 기준은 |r| ≥ {corr.min_abs_r.toFixed(2)}이고 이 페이지의 기본 임계는{' '}
                    {DEFAULT_MIN_WEIGHT.toFixed(2)}입니다 —{' '}
                    <strong>|r| 이 그 사이인 엣지는 기본 화면에 그려지지 않습니다.</strong>{' '}
                    {corrTypeOn ? (
                      <>
                        지금 임계 {minWeight.toFixed(2)}에서 그린 CORRELATED 는{' '}
                        <span className="num">{int(nCorrEdges)}</span>건이며, 전체 그래프에서 임계를{' '}
                        {corr.min_abs_r.toFixed(2)}까지 내리면{' '}
                        <span className="num">{int(corr.n_edges)}</span>건이 모두 보입니다.
                      </>
                    ) : (
                      <>
                        지금은 "이상률 동조" 타입이 꺼져 있어 CORRELATED 를 그리지 않습니다 — 타입을
                        켜고 전체 그래프에서 임계를 {corr.min_abs_r.toFixed(2)}까지 내리면{' '}
                        <span className="num">{int(corr.n_edges)}</span>건이 모두 보입니다.
                      </>
                    )}
                  </p>
                )}
                <dl className="apanel__stats">
                  <div>
                    <dt>검정한 쌍</dt>
                    <dd className="num">
                      {int(corr.n_pairs_tested)} / {int(corr.n_pairs_total)}
                    </dd>
                  </div>
                  <div>
                    <dt>창이 짧아 못 한 쌍</dt>
                    <dd className="num">{int(corr.n_pairs_short_window)}</dd>
                  </div>
                  <div>
                    <dt>관측일 · 최대 공통</dt>
                    <dd className="num">
                      {int(corr.n_observed_days)}일 · {int(corr.max_common_days)}일
                    </dd>
                  </div>
                  <div>
                    <dt>최대 차분 표본</dt>
                    <dd className="num">
                      {int(corr.max_diff_pairs)}개 (기준 {int(corr.min_days_required)})
                    </dd>
                  </div>
                  <div>
                    <dt>차분이 지운 허위 엣지</dt>
                    <dd className="num">{int(corr.n_spurious_removed_by_diff)}</dd>
                  </div>
                </dl>
                {corr.n_pairs_tested === 0 ? (
                  <p className="apanel__why">
                    마지막 줄을 <strong>"허위 엣지가 없었다"로 읽으면 안 됩니다.</strong> 검정한
                    쌍이 {int(corr.n_pairs_tested)}개라 뺄 것 자체가 없었습니다. 기준(|r| ≥{' '}
                    {corr.min_abs_r.toFixed(2)} · BH-FDR q &lt; {corr.fdr_q.toFixed(2)})을 낮춰
                    엣지를 만들지 않고 0건으로 둡니다.
                  </p>
                ) : (
                  <p className="apanel__why">
                    마지막 줄은 차분 전 상관으로는 기준을 넘었지만 <strong>1차 차분 뒤 탈락한 쌍</strong>
                    입니다. 계절·요일·API 장애 같은 공통 추세가 만든 관계라 엣지로 세지 않았습니다.
                  </p>
                )}
              </>
            ) : (
              <p className="apanel__why">
                진단값(/meta/manifest 의 graph.correlation)을 불러오지 못했습니다. 사유를 지어내지
                않고 비워 둡니다.
              </p>
            )}
          </section>

          <section className="apanel__block">
            <h3 className="apanel__title">
              고립 노드 <span className="num">{int(g.isolated.length)}</span>곳
            </h3>
            {/*
              사유가 섞여 있는데 설명이 하나면 거짓이 된다. 임계를 올리면 좌표가 군집
              안에 있는 대형 사업자도 여기로 내려오는데, 그들에게 "군집 밖으로
              밀려났다"는 설명은 사실이 아니다 — 종류별로 세어 따로 적는다.
            */}
            <p className="apanel__why">
              <strong>화면에서 빠진 것이지 데이터에서 지운 것이 아닙니다.</strong> 빠진 사유는 한
              가지가 아니라 아래처럼 나뉩니다.
            </p>
            <ul className="apanel__isowhylist" data-testid="isolated-why">
              {nIsoDropped > 0 && (
                <li>
                  <b className="num">{int(nIsoDropped)}곳</b> — 빌드가 엣지 계산에서 뺀 노드입니다.
                  ForceAtlas2 가 이들을 군집 밖 수천 단위로 밀어내 전체 범위로 화면을 맞추면 본
                  군집이 점 하나가 되므로, <strong>연결 노드 기준으로 화면을 맞췄습니다.</strong>
                </li>
              )}
              {nIsoThreshold > 0 && (
                <li>
                  <b className="num">{int(nIsoThreshold)}곳</b> — 좌표는 <strong>군집 안</strong>인데
                  현재 임계 {minWeight.toFixed(2)}에서 그릴 선이 없어 빠졌습니다. 화면 맞춤과는
                  무관하며 임계를 낮추면 제자리로 돌아옵니다.
                </li>
              )}
              {nIsoNoXy > 0 && (
                <li>
                  <b className="num">{int(nIsoNoXy)}곳</b> — 레이아웃 산출물에 좌표가 없어 그릴 자리
                  자체를 모릅니다. 자리를 지어내지 않고 목록으로만 둡니다.
                </li>
              )}
            </ul>
            {g.isolated.length === 0 ? (
              <p className="apanel__why">이 조건에서는 고립 노드가 없습니다.</p>
            ) : (
              // 목록이 길어 스크롤 영역이 된다 — 키보드로도 굴릴 수 있어야 하므로
              // 초점을 받게 한다 (axe scrollable-region-focusable)
              <ul className="apanel__iso" data-testid="isolated-list" tabIndex={0}>
                {g.isolated.map((iso) => (
                  <li key={iso.node.node_id}>
                    <span className="apanel__isoname">{iso.node.label}</span>
                    <span className="apanel__isometa num">
                      충전기 {int(iso.node.n_chargers)}기 · 커버리지 {pct(iso.node.coverage, 1)} ·
                      가동률 {pct(iso.node.availability)}
                    </span>
                    <span className="apanel__isowhy">{iso.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="apanel__block">
            <h3 className="apanel__title">이 그림을 만든 것</h3>
            <dl className="apanel__stats">
              <div>
                <dt>레이아웃</dt>
                <dd className="mono">{g.layoutId ?? '좌표 없음 / 혼재'}</dd>
              </div>
              {layout && (
                <>
                  <div>
                    <dt>반복 · 시드</dt>
                    <dd className="num">
                      {int(layout.iterations)}회 · {layout.seed}
                    </dd>
                  </div>
                  <div>
                    <dt>빌드가 센 고립</dt>
                    <dd className="num">{int(layout.n_isolated)}곳</dd>
                  </div>
                </>
              )}
              {sim && (
                <>
                  <div>
                    <dt>유사도 점수 대상</dt>
                    <dd className="num">
                      {int(sim.n_operators_scored)} / {int(sim.n_operators_total)}
                    </dd>
                  </div>
                  <div>
                    <dt>코사인 하한</dt>
                    <dd className="num">{sim.min_cosine.toFixed(2)}</dd>
                  </div>
                </>
              )}
              <div>
                <dt>수준</dt>
                <dd className="mono">{g.level}</dd>
              </div>
            </dl>
            <p className="apanel__why">
              레이아웃 이름을 하드코딩하지 않고 응답에서 읽습니다 — 엣지가 없어 원형으로 배치한
              것과 레이아웃이 실패한 것을 구분할 수 있어야 하기 때문입니다 (§8.1.1).
            </p>
          </section>
        </aside>
      </SidePanel>
    </div>
  )
}
